import { useMemo } from "react";
import dayjs from "dayjs";

import TimelineHeaderControls from "@/components/Timeline/TimelineHeaderControls";
import TimelineLibrary from "@/components/Timeline/TimelineLibrary";
import { useTimelineControls } from "@/components/Timeline/useTimelineControls";
import type { TimelineItem } from "@/components/Timeline/timeline";

import type { PlanOfRecordMilestone } from "@/views/PlanOfRecord/planOfRecord.types";
import {
  PLAN_OF_RECORD_TIMELINE_COLUMNS,
  TIMELINE_STATUS_LABEL,
  planOfRecordTimelineUtils,
} from "@/views/PlanOfRecord/planOfRecordTimelineUtils";

const TIMELINE_COLUMNS = PLAN_OF_RECORD_TIMELINE_COLUMNS.map((fieldName) => ({
  fieldName,
  visibility: "visible" as const,
}));

/**
 * The same milestones on a time axis. Uses the shared timeline rather than a second Gantt
 * implementation, so it behaves like the one in Trial Planning.
 *
 * A milestone is a point in time, not a span, so each bar runs from its planned date to its
 * actual date where one exists — which makes slippage the visible length of the bar — and is
 * a marker at the planned date where it does not.
 */
export function MilestoneGantt({
  milestones,
}: {
  milestones: PlanOfRecordMilestone[];
}) {
  const rows = useMemo<TimelineItem[]>(
    () =>
      milestones.map((milestone) => {
        const status = TIMELINE_STATUS_LABEL[milestone.status];
        return {
          /**
           * The library colours a row from `core_table`, not `event_type`, so the status
           * goes here. The library also guards a reversed range itself, so planned and
           * actual can be passed as-is and the columns stay honest about which is which.
           */
          core_table: status,
          event_name: milestone.label,
          event_type: status,
          start_date: milestone.plannedDate,
          end_date: milestone.actualDate ?? milestone.plannedDate,
          ongoing_derived: milestone.actualDate === null,
          trial: milestone.scopeLabel,
        };
      }),
    [milestones],
  );

  const firstEventDate = useMemo(() => {
    const earliest = rows
      .map((row) => row.start_date)
      .sort((a, b) => a.localeCompare(b))[0];
    return earliest ? new Date(`${earliest}T00:00:00`) : null;
  }, [rows]);

  const controls = useTimelineControls({ firstEventDate });

  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center rounded-[8px] border border-dashed border-[#D1D5DB] bg-white py-8">
        <p className="text-[13px] text-[#6B7280]">
          Add milestones to see them on a timeline.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-[8px] border border-[#F0F2F4] bg-white p-3 shadow-[0_2px_8px_rgba(15,23,42,0.06)]">
      <TimelineHeaderControls
        viewMode={controls.viewMode}
        selectedDate={controls.selectedDate}
        isTodayActive={controls.isTodayActive}
        isFirstEventActive={controls.isFirstEventActive}
        hasFirstEventDate={!!firstEventDate}
        minSelectableDate={firstEventDate}
        maxSelectableDate={null}
        testIdPrefix="plan-of-record-timeline"
        onViewModeChange={controls.handleViewModeChange}
        onDateChange={controls.handleDateChange}
        onTodayClick={controls.handleTodayClick}
        onEarliestClick={controls.handleEarliestClick}
      />
      <TimelineLibrary
        data={rows}
        viewMode={controls.viewMode}
        viewDate={controls.viewDate}
        centerOnDate={controls.centerOnDate}
        centerOnDateRequestId={controls.centerOnDateRequestId}
        columns={TIMELINE_COLUMNS}
        utils={planOfRecordTimelineUtils}
        viewportOffset={260}
        onHorizontalScroll={controls.handleHorizontalScroll}
        onRowSelect={(item) =>
          controls.handleDateChange(dayjs(item.start_date))
        }
      />
    </div>
  );
}
